import type { MatterViewModel } from '~/modules/matters/matters.types';
import {
  expertHasActiveParticipationInMatterExperts,
  getFirstHiredMatterExpertRow,
  getHiredExpertIds,
  getInvitedExpertIds,
} from '~/modules/matters/matterExpertUtils';
import {
  expertUserForMatterExpertId,
  expertUserFromMatterExpertRow,
} from '~/modules/matters/matterParticipantDisplay';
import { RetainerStatus, type RetainerFieldsFragment } from '~/gql/graphql';
import type { User } from '~/shared/types/connect';
import { formatActiveRetainersDisplay } from '~/shared/utils/formatRetainers';
import {
  ExpertRetainerRollupStatus,
  type ExpertRetainerStatus,
} from '~/modules/matters/MatterCard.types';

export interface GetExpertsOnMatterInput {
  matter: MatterViewModel;
  /** Retainers for this matter from `retainersForMatter`. */
  retainers: RetainerFieldsFragment[];
  getExpertById: (id: string) => User | undefined;
}

export interface GetExpertsOnMatterResult {
  hiredExpertIds: string[];
  hiredExperts: User[];
  addedExperts: User[];
  hasAnyHiredExpert: boolean;
  expertRetainerStatuses: ExpertRetainerStatus[];
}

export type ExpertsOnMatterResult = GetExpertsOnMatterResult;

/** True when the expert has an invited or hired row (not declined/removed only). */
export function matterHasActiveExpertParticipation(
  matter: MatterViewModel,
  expertId: string,
): boolean {
  return expertHasActiveParticipationInMatterExperts(
    matter.matterExperts,
    expertId,
  );
}

function retainerExpertId(retainer: RetainerFieldsFragment): string {
  return retainer.matterExpert.expertId;
}

export function getExpertsOnMatter(
  input: GetExpertsOnMatterInput,
): GetExpertsOnMatterResult {
  const { matter, retainers, getExpertById } = input;

  const pendingRequestsForMatter = retainers.filter(
    (retainer) => retainer.status === RetainerStatus.Pending,
  );
  const activeEngagementRetainers = retainers.filter(
    (retainer) =>
      retainer.status === RetainerStatus.Approved && !retainer.workCompletedAt,
  );
  const completedRequestsForMatter = retainers.filter((retainer) => {
    if (retainer.status === RetainerStatus.Approved) {
      return Boolean(retainer.workCompletedAt);
    }
    return (
      retainer.status === RetainerStatus.Closed ||
      retainer.status === RetainerStatus.Declined
    );
  });

  const matterHiredIds = getHiredExpertIds(matter.matterExperts);
  const hiredExpertIds = [
    ...new Set([
      ...matterHiredIds,
      ...activeEngagementRetainers.map((retainer) =>
        retainerExpertId(retainer),
      ),
    ]),
  ];
  const hiredExperts = hiredExpertIds
    .map((id) => expertUserForMatterExpertId(matter, id, getExpertById))
    .filter((user): user is User => user !== undefined);

  const matterInvitedIds = getInvitedExpertIds(matter.matterExperts);
  const addedExpertIds = [
    ...new Set([
      ...pendingRequestsForMatter.map((request) => retainerExpertId(request)),
      ...matterInvitedIds,
      ...matterHiredIds.filter((id) => !hiredExpertIds.includes(id)),
    ]),
  ].filter((id) => !hiredExpertIds.includes(id));

  const addedExperts = addedExpertIds
    .map((id) => expertUserForMatterExpertId(matter, id, getExpertById))
    .filter((user): user is User => user !== undefined);

  const footerExpertIds = [
    ...new Set([
      ...activeEngagementRetainers.map((retainer) =>
        retainerExpertId(retainer),
      ),
      ...pendingRequestsForMatter.map((request) => retainerExpertId(request)),
      ...matterHiredIds,
      ...matterInvitedIds,
    ]),
  ];
  const footerExperts = footerExpertIds
    .map((id) => expertUserForMatterExpertId(matter, id, getExpertById))
    .filter((user): user is User => user !== undefined && user !== null);

  const expertRetainerStatuses: ExpertRetainerStatus[] = footerExperts.map(
    (expert) => {
      const hasActive = activeEngagementRetainers.some(
        (activeRetainer) => retainerExpertId(activeRetainer) === expert.id,
      );
      const hasPending = pendingRequestsForMatter.some(
        (request) => retainerExpertId(request) === expert.id,
      );
      const hasCompleted = completedRequestsForMatter.some(
        (request) => retainerExpertId(request) === expert.id,
      );
      let status = ExpertRetainerRollupStatus.WaitingRequest;
      if (hasActive) {
        status = ExpertRetainerRollupStatus.Active;
      } else if (hasPending) {
        status = ExpertRetainerRollupStatus.PendingApproval;
      } else if (hasCompleted) {
        status = ExpertRetainerRollupStatus.Completed;
      }
      return { expertId: expert.id, status };
    },
  );

  return {
    hiredExpertIds,
    hiredExperts,
    addedExperts,
    hasAnyHiredExpert: hiredExpertIds.length > 0,
    expertRetainerStatuses,
  };
}

export interface RetainerStatusDisplay {
  label: string;
  color: 'red' | 'warning' | 'success';
}

export interface MatterCardDerivedInput {
  /** Retainers for this matter (e.g. from `matter.matterExperts[].retainers` on the matters query). */
  retainers: RetainerFieldsFragment[];
  getExpertById: (id: string) => User | undefined;
}

export interface MatterCardDerivedResult {
  otherParty: User | null;
  hasPendingRetainer: boolean;
  hasFundedRetainer: boolean;
  activeRetainerAmount: string;
  activeRetainerCount: number;
  isPendingRetainerInitiatedByExpert: boolean;
  experts: User[];
  expertRetainerStatuses: ExpertRetainerStatus[];
}

export function getMatterCardDerivedProps(
  matter: MatterViewModel,
  input: MatterCardDerivedInput,
): MatterCardDerivedResult {
  const { retainers, getExpertById } = input;

  const pendingRequestsForMatter = retainers.filter(
    (retainer) => retainer.status === RetainerStatus.Pending,
  );
  const pendingRetainerRequest = pendingRequestsForMatter[0];

  const activeRetainers = retainers.filter(
    (retainer) =>
      retainer.status === RetainerStatus.Approved && !retainer.workCompletedAt,
  );

  let otherParty: User | null = null;
  const firstHiredRow = getFirstHiredMatterExpertRow(matter.matterExperts);
  if (firstHiredRow) {
    otherParty = expertUserFromMatterExpertRow(firstHiredRow, getExpertById);
  } else if (pendingRetainerRequest) {
    otherParty =
      expertUserForMatterExpertId(
        matter,
        pendingRetainerRequest.matterExpert.expertId,
        getExpertById,
      ) ?? null;
  }

  const hasPendingRetainer = Boolean(pendingRetainerRequest);
  const hasFundedRetainer = activeRetainers.length > 0;

  const activeRetainerAmount = formatActiveRetainersDisplay(
    activeRetainers.map((activeRetainer) => {
      const hoursPurchased = parseHoursRequestedForCard(
        activeRetainer.hoursRequested,
      );
      const flatCents = activeRetainer.flatFee ?? 0;
      const flatAmountPurchased = flatCents > 0 ? flatCents / 100 : undefined;
      return {
        hoursPurchased,
        flatAmountPurchased,
        expertName: expertUserForMatterExpertId(
          matter,
          activeRetainer.matterExpert.expertId,
          getExpertById,
        )?.name,
        title: activeRetainer.title,
      };
    }),
    { summaryOnly: true },
  );

  const matterHiredIdsForFooter = getHiredExpertIds(matter.matterExperts);
  const matterInvitedIdsForFooter = getInvitedExpertIds(matter.matterExperts);
  const footerExpertIds = [
    ...new Set([
      ...activeRetainers.map(
        (activeRetainer) => activeRetainer.matterExpert.expertId,
      ),
      ...pendingRequestsForMatter.map(
        (request) => request.matterExpert.expertId,
      ),
      ...matterHiredIdsForFooter,
      ...matterInvitedIdsForFooter,
    ]),
  ];
  const footerExperts = footerExpertIds
    .map((id) => expertUserForMatterExpertId(matter, id, getExpertById))
    .filter((user): user is User => user !== undefined && user !== null);

  const expertRetainerStatuses: ExpertRetainerStatus[] = footerExperts.map(
    (expert) => {
      const hasActive = activeRetainers.some(
        (activeRetainer) => activeRetainer.matterExpert.expertId === expert.id,
      );
      const hasPending = pendingRequestsForMatter.some(
        (request) => request.matterExpert.expertId === expert.id,
      );
      let status = ExpertRetainerRollupStatus.WaitingRequest;
      if (hasActive) {
        status = ExpertRetainerRollupStatus.Active;
      } else if (hasPending) {
        status = ExpertRetainerRollupStatus.PendingApproval;
      }
      return { expertId: expert.id, status };
    },
  );

  const isPendingRetainerInitiatedByExpert = pendingRequestsForMatter.some(
    (retainer) =>
      Boolean(retainer.expertAgreedAt) && !retainer.attorneyAgreedAt,
  );
  const experts = footerExperts;

  return {
    otherParty,
    hasPendingRetainer,
    hasFundedRetainer,
    activeRetainerAmount,
    activeRetainerCount: activeRetainers.length,
    isPendingRetainerInitiatedByExpert,
    experts,
    expertRetainerStatuses,
  };
}

function parseHoursRequestedForCard(value: string | null | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getRetainerStatusDisplay(
  status: ExpertRetainerRollupStatus | null | undefined,
): RetainerStatusDisplay | null {
  if (status === ExpertRetainerRollupStatus.PendingApproval) {
    return { label: 'Pending approval', color: 'red' };
  }
  if (status === ExpertRetainerRollupStatus.WaitingRequest) {
    return { label: 'Waiting for request', color: 'warning' };
  }
  if (status === ExpertRetainerRollupStatus.Active) {
    return { label: 'Active retainer', color: 'success' };
  }
  return null;
}
